/**
 * Sorting for tables whose headers sort on click (DataView `sort` / `onSort`, 29 Sep 2026 —
 * first used by the Promoters tab). Pure, so the rules are tested once for every table.
 *
 * A sort is `{ key, dir }`, dir "asc" | "desc".
 *  - Clicking the column already sorted flips its direction; clicking another column starts it
 *    in that column's natural direction (text A→Z, numbers and dates biggest/newest first).
 *  - Empty values (null, undefined, "", an invalid date) always sort LAST, in either direction:
 *    "no visit yet" should never top a list of most recent visits.
 *  - Ties keep a stable secondary order (the tie-break accessor, e.g. the name).
 */

/** The sort after clicking `key`'s header. */
export function nextSort(current, key, firstDir = "desc") {
  if (current && current.key === key) {
    return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: firstDir };
}

/** A stored sort is only used if it names a known column and a real direction. */
export function validSort(stored, keys, fallback) {
  return stored && keys.includes(stored.key) && (stored.dir === "asc" || stored.dir === "desc")
    ? stored
    : fallback;
}

const isEmpty = (v) =>
  v === null || v === undefined || v === "" || (v instanceof Date && Number.isNaN(v.getTime()));

function compareValues(a, b) {
  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b, "en-GB", { sensitivity: "base", numeric: true });
  }
  const x = a instanceof Date ? a.getTime() : a;
  const y = b instanceof Date ? b.getTime() : b;
  return x < y ? -1 : x > y ? 1 : 0;
}

/**
 * A sorted copy of `rows`.
 * @param accessors  { [key]: (row) => number | string | Date | null }
 * @param tieBreak   (row) => string, ascending, applied when the sort key ties
 */
export function sortRows(rows, sort, accessors, tieBreak) {
  const get = sort && accessors[sort.key];
  if (!get) return [...rows];
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((r1, r2) => {
    const a = get(r1);
    const b = get(r2);
    const ea = isEmpty(a);
    const eb = isEmpty(b);
    if (ea !== eb) return ea ? 1 : -1; // empties last, whatever the direction
    const c = ea ? 0 : sign * compareValues(a, b);
    if (c !== 0 || !tieBreak) return c;
    return compareValues(String(tieBreak(r1) ?? ""), String(tieBreak(r2) ?? ""));
  });
}
